"use client";

import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {ApprovalStatusBanner, type ApprovalVisualState} from "@repo/ui/gailgpt/approval-status-banner";
import JsonDisclosure from "./JsonDisclosure";
import WorkflowPublicationSteps from "./WorkflowPublicationSteps";
import type {PublicationWorkflow, workflowPublicationSummary} from "./workflowPublicationSummary";

/** Shared presentation for saved workflows and publication approvals. */
export default function WorkflowCard({workflow, summary, title = workflow.name, status, state, publishState, label, showSteps = true, configuration = workflow, children}: {
   workflow: PublicationWorkflow;
   summary: ReturnType<typeof workflowPublicationSummary>;
   title?: string;
   status: ReactNode;
   state?: ApprovalVisualState;
   publishState?: string;
   label?: string;
   showSteps?: boolean;
   configuration?: unknown;
   children: ReactNode;
}) {
   const {t} = useTranslation("ask");
   return <article className="approval-card my-3 w-full overflow-hidden rounded-xl border [&_.approval-banner_output]:flex-wrap [&_.approval-open]:text-[13px] [&_.approval-open]:font-medium [&_.approval-open]:leading-5" data-publish-state={publishState} data-approval-state={state} aria-label={label}>
      <ApprovalStatusBanner state={state}>{status}</ApprovalStatusBanner>
      <header className="approval-heading">
         <h3 className="approval-title [overflow-wrap:anywhere]">{title}</h3>
         <p className="approval-meta">{t("publishWorkflow.workflow")}{workflow.revision != null && <> · {t("workflowPanel.revision", {revision: workflow.revision})}</>} · {t("workflowPanel.steps", {count: summary.stepCount})}</p>
      </header>
      <dl className="grid grid-cols-[64px_minmax(0,1fr)] gap-x-2.5 gap-y-1.5 px-4 pb-3.5 text-[13px] leading-[19px] [overflow-wrap:anywhere] [&_dt]:text-xs [&_dt]:font-medium [&_dt]:text-[var(--approval-muted)]">
         <dt>{t("publishWorkflow.runs")}</dt><dd>{summary.runs}</dd>
         <dt>{t("publishWorkflow.does")}</dt><dd>{summary.summary}</dd>
      </dl>
      {showSteps && <WorkflowPublicationSteps summary={summary} />}
      <div className="px-4 pb-3.5">
         <JsonDisclosure label={t("toolApproval.workflow.fullConfiguration")} value={configuration} />
      </div>
      {children}
   </article>;
}

/** Shared footer layout, scoped to the workflow card's content. */
export function WorkflowCardFooter({compact = false, children}: {compact?: boolean; children: ReactNode}) {
   return <footer className={`flex flex-col gap-2.5 border-t px-4 ${compact ? "py-2.5" : "py-3"} [&_p]:text-xs [&_p]:leading-[18px] [&_p:not([role=alert])]:text-[var(--approval-muted)] [&>[role=alert]]:m-0 [&>[role=note]]:m-0 [&>output]:m-0`}>
      {children}
   </footer>;
}
